// Bug Sources — sprint-over-sprint view of completed bugs: where they were found
// (environment), priority (P0–P4), functional area, who filed them, and the top
// epics driving each sprint. Data from GET /api/pulse/bug-insights.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { PULSE_COLORS } from './pulseConfig.js'

const PRIO_KEYS = ['P0', 'P1', 'P2', 'P3', 'P4']
const PRIO_COLOR = { P0: '#b91c1c', P1: '#f97316', P2: '#eab308', P3: '#38bdf8', P4: '#94a3b8' }
const ENV_KEYS = ['production', 'lower', 'unspecified']
const ENV_META = {
    production: { label: 'Production (escaped)', color: '#ef4444' },
    lower: { label: 'Lower env (QA-caught)', color: '#10b981' },
    unspecified: { label: 'Unspecified', color: '#475569' },
}
const AREA_PALETTE = ['#3b82f6', '#a78bfa', '#f59e0b', '#10b981', '#ef4444', '#06b6d4', '#ec4899', '#84cc16', '#fb923c', '#64748b']
const MAX_AREAS = 8

export default function BugSources() {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [selSprint, setSelSprint] = useState(null)

    const load = useCallback(async (refresh = false) => {
        setLoading(true)
        setError(null)
        try {
            const r = await fetch(`/api/pulse/bug-insights${refresh ? '?refresh=1' : ''}`)
            const j = await r.json()
            if (!r.ok) throw new Error(j?.error ?? `bug-insights ${r.status}`)
            setData(j)
            if (j.sprints?.length) setSelSprint(j.sprints[j.sprints.length - 1].sprintNum)
        } catch (e) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { load(false) }, [load])

    // Areas to show as their own series (top MAX_AREAS); rest fold into "Other".
    const areaSeries = useMemo(() => {
        if (!data?.areaOrder) return []
        return data.areaOrder.slice(0, MAX_AREAS)
    }, [data])

    const prioRows = useMemo(() => (data?.sprints || []).map((s) => ({
        sprint: `S${s.sprintNum}`, sprintNum: s.sprintNum, ...PRIO_KEYS.reduce((o, k) => ({ ...o, [k]: s.priority[k] || 0 }), {}),
    })), [data])

    const envRows = useMemo(() => (data?.sprints || []).map((s) => ({
        sprint: `S${s.sprintNum}`, sprintNum: s.sprintNum, ...ENV_KEYS.reduce((o, k) => ({ ...o, [k]: s.env[k] || 0 }), {}),
    })), [data])

    const areaRows = useMemo(() => (data?.sprints || []).map((s) => {
        const row = { sprint: `S${s.sprintNum}`, sprintNum: s.sprintNum }
        let other = 0
        for (const [area, n] of Object.entries(s.areas || {})) {
            if (areaSeries.includes(area)) row[area] = (row[area] || 0) + n
            else other += n
        }
        if (other) row.Other = other
        return row
    }), [data, areaSeries])

    const kpis = useMemo(() => {
        if (!data?.sprints?.length) return null
        const prodEscapes = data.sprints.reduce((s, x) => s + (x.env.production || 0), 0)
        const p0p1 = data.sprints.reduce((s, x) => s + (x.priority.P0 || 0) + (x.priority.P1 || 0), 0)
        const latest = data.sprints[data.sprints.length - 1]
        return { total: data.totalBugs, prodEscapes, p0p1, latest }
    }, [data])

    const selected = useMemo(
        () => (data?.sprints || []).find((s) => s.sprintNum === selSprint) || null,
        [data, selSprint]
    )

    const onBarClick = (e) => { if (e?.activePayload?.[0]?.payload?.sprintNum) setSelSprint(e.activePayload[0].payload.sprintNum) }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <div>
                    <h2 style={{ color: PULSE_COLORS.text, margin: 0, fontSize: '20px' }}>🐞 Bug Sources</h2>
                    <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', maxWidth: '860px', marginTop: '4px' }}>
                        Completed bugs in BP/SP/FP, attributed to the sprint they were fixed in. Where they were found
                        (environment), priority, functional area, and who filed them — sprint over sprint. Click any bar
                        to drill into a sprint.
                    </p>
                </div>
                <button
                    onClick={() => load(true)}
                    disabled={loading}
                    style={{
                        background: PULSE_COLORS.primary, color: PULSE_COLORS.text, border: 'none',
                        borderRadius: '4px', padding: '8px 14px', fontSize: '13px', fontWeight: 600,
                        cursor: loading ? 'wait' : 'pointer', whiteSpace: 'nowrap',
                    }}
                >
                    {loading ? 'Loading…' : '↻ Refresh'}
                </button>
            </div>

            {error && <div style={{ color: PULSE_COLORS.danger, fontSize: '13px' }}>{error}</div>}
            {loading && !data && <div style={{ color: PULSE_COLORS.textMuted }}>Pulling bugs from Jira…</div>}

            {kpis && (
                <>
                    {/* KPI row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                        <Kpi label="Total bugs (last ~16 sprints)" value={kpis.total} />
                        <Kpi label="Confirmed prod escapes" value={kpis.prodEscapes} color="#ef4444"
                            hint="Bugs with an explicit production marker — a prod label, [Prod] in the title, or PROD in the description's Env: declaration. A floor; bugs with no env declaration aren't counted." />
                        <Kpi label="P0 + P1 (high severity)" value={kpis.p0p1} color="#f97316" />
                        <Kpi label={`Latest sprint (S${kpis.latest.sprintNum})`} value={kpis.latest.total} color={PULSE_COLORS.primary} />
                    </div>

                    {/* Environment note */}
                    <div style={{ fontSize: '12px', color: PULSE_COLORS.textSubtle, background: PULSE_COLORS.bgInner, border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '6px', padding: '8px 12px' }}>
                        Environment is read from the <strong>Env:</strong> declaration in the bug description (e.g. <code>Env: Staging</code> or
                        <code>PROD, STAGING, DEV</code>), plus prod labels and <code>[Prod]</code> title tags. Bugs with no declaration land in
                        <strong>Unspecified</strong> — the reporter pattern (QA engineers) suggests these are mostly pre-prod QA finds.
                        <strong>Production (escaped)</strong> counts only bugs with an explicit prod marker, so treat it as a floor.
                    </div>

                    <ChartCard title="Bugs per sprint — by Priority">
                        <StackedBars rows={prioRows} keys={PRIO_KEYS} colorOf={(k) => PRIO_COLOR[k]} onClick={onBarClick} selSprint={selSprint} />
                    </ChartCard>

                    <ChartCard title="Bugs per sprint — by Environment found">
                        <StackedBars rows={envRows} keys={ENV_KEYS} colorOf={(k) => ENV_META[k].color} nameOf={(k) => ENV_META[k].label} onClick={onBarClick} selSprint={selSprint} />
                    </ChartCard>

                    <ChartCard title="Bugs per sprint — by Functional area">
                        <StackedBars
                            rows={areaRows}
                            keys={[...areaSeries, 'Other']}
                            colorOf={(k) => (k === 'Other' ? '#475569' : AREA_PALETTE[areaSeries.indexOf(k) % AREA_PALETTE.length])}
                            onClick={onBarClick}
                            selSprint={selSprint}
                        />
                    </ChartCard>

                    {/* Sprint detail */}
                    {selected && <SprintDetail s={selected} />}

                    {/* Who filed — overall */}
                    <ChartCard title="Who files the bugs (all sprints)">
                        <RankTable rows={data.topReporters} unit="bugs" />
                    </ChartCard>
                </>
            )}

            {data?.generatedAt && (
                <div style={{ fontSize: '11px', color: PULSE_COLORS.textSubtle }}>
                    Generated {new Date(data.generatedAt).toLocaleString()}{data.cached ? ' (cached ≤30 min)' : ''}
                </div>
            )}
        </div>
    )
}

function Kpi({ label, value, color, hint }) {
    return (
        <div title={hint || ''} style={{ background: PULSE_COLORS.cardBg, border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '8px', padding: '14px 16px' }}>
            <div style={{ fontSize: '12px', color: PULSE_COLORS.textMuted, marginBottom: '6px' }}>{label}</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: color || PULSE_COLORS.text }}>{value}</div>
        </div>
    )
}

function ChartCard({ title, children }) {
    return (
        <div style={{ background: PULSE_COLORS.cardBg, border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '8px', padding: '14px 16px' }}>
            <div style={{ color: PULSE_COLORS.text, fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>{title}</div>
            {children}
        </div>
    )
}

function StackedBars({ rows, keys, colorOf, nameOf, onClick, selSprint }) {
    return (
        <ResponsiveContainer width="100%" height={300}>
            <BarChart data={rows} onClick={onClick} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={PULSE_COLORS.border} vertical={false} />
                <XAxis dataKey="sprint" stroke={PULSE_COLORS.textMuted} fontSize={11} />
                <YAxis stroke={PULSE_COLORS.textMuted} fontSize={11} allowDecimals={false} />
                <Tooltip
                    contentStyle={{ background: PULSE_COLORS.bgInner, border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '6px', fontSize: '12px' }}
                    labelStyle={{ color: PULSE_COLORS.text }}
                    cursor={{ fill: `${PULSE_COLORS.primary}15` }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                {keys.map((k) => (
                    <Bar
                        key={k} dataKey={k} stackId="a" name={nameOf ? nameOf(k) : k} fill={colorOf(k)}
                        cursor="pointer"
                        opacity={selSprint == null ? 1 : 0.9}
                    />
                ))}
            </BarChart>
        </ResponsiveContainer>
    )
}

function SprintDetail({ s }) {
    const envTotal = ENV_KEYS.reduce((a, k) => a + (s.env[k] || 0), 0) || 1
    return (
        <div style={{ background: PULSE_COLORS.cardBg, border: `1px solid ${PULSE_COLORS.primary}`, borderRadius: '8px', padding: '16px' }}>
            <div style={{ color: PULSE_COLORS.text, fontSize: '15px', fontWeight: 700, marginBottom: '12px' }}>
                Sprint {s.sprintNum} — {s.total} bugs
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                <div>
                    <Sub title="Likely drivers (top epics)">
                        <RankTable rows={s.topEpics} unit="bugs" truncate={52} />
                    </Sub>
                    <Sub title="Top areas">
                        <RankTable rows={s.topAreas} unit="bugs" />
                    </Sub>
                </div>
                <div>
                    <Sub title="Who filed them">
                        <RankTable rows={s.topReporters} unit="bugs" />
                    </Sub>
                    <Sub title="Severity & environment">
                        <div style={{ fontSize: '12px', color: PULSE_COLORS.textMuted, lineHeight: 1.9 }}>
                            {PRIO_KEYS.filter((k) => s.priority[k]).map((k) => (
                                <span key={k} style={{ marginRight: '12px' }}>
                                    <span style={{ color: PRIO_COLOR[k], fontWeight: 700 }}>{k}</span> {s.priority[k]}
                                </span>
                            ))}
                            <div style={{ marginTop: '6px' }}>
                                {ENV_KEYS.map((k) => (
                                    <span key={k} style={{ marginRight: '12px' }}>
                                        <span style={{ color: ENV_META[k].color, fontWeight: 700 }}>{ENV_META[k].label}</span>{' '}
                                        {s.env[k] || 0} ({Math.round(((s.env[k] || 0) / envTotal) * 100)}%)
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Sub>
                </div>
            </div>
        </div>
    )
}

function Sub({ title, children }) {
    return (
        <div style={{ marginBottom: '16px' }}>
            <div style={{ color: PULSE_COLORS.textSubtle, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>{title}</div>
            {children}
        </div>
    )
}

function RankTable({ rows, unit, truncate }) {
    if (!rows?.length) return <div style={{ color: PULSE_COLORS.textSubtle, fontSize: '12px' }}>—</div>
    const max = Math.max(...rows.map((r) => r.count), 1)
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {rows.map((r) => (
                <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                    <span style={{ color: PULSE_COLORS.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {truncate ? String(r.key).slice(0, truncate) : r.key}
                    </span>
                    <span style={{ width: '90px', height: '8px', background: PULSE_COLORS.bgInner, borderRadius: '4px', overflow: 'hidden' }}>
                        <span style={{ display: 'block', width: `${(r.count / max) * 100}%`, height: '100%', background: PULSE_COLORS.primary }} />
                    </span>
                    <span style={{ color: PULSE_COLORS.textMuted, width: '52px', textAlign: 'right' }}>{r.count} {unit}</span>
                </div>
            ))}
        </div>
    )
}
